import { apiClient } from '../apiClient';
import {
  StateMachineValidationResult,
  TransitionValidationResult,
  AvailableTransition,
  TransitionExecuteRequest,
  TransitionRollbackRequest,
} from '../../types/stateMachine';
import { ArtifactItemState, ArtifactItemTransition } from '../../types/artifactItems';
import { API_ENDPOINTS } from './config';

/**
 * Service for state machine operations including validation, transitions, and rollback
 */
class StateMachineService {
  /**
   * Validate a state machine for completeness, reachability, and consistency
   */
  static async validateStateMachine(
    artifactId: string,
    options: {
      validate_completeness?: boolean;
      validate_reachability?: boolean;
      validate_consistency?: boolean;
      identify_bottlenecks?: boolean;
    } = {}
  ): Promise<StateMachineValidationResult> {
    try {
      const defaultOptions = {
        validate_completeness: true,
        validate_reachability: true,
        validate_consistency: true,
        identify_bottlenecks: false,
        ...options,
      };

      // Backend expects query params (state_machine_analysis.py), not body
      const response = await apiClient.post(
        API_ENDPOINTS.STATE_MACHINE_VALIDATE(artifactId),
        {},
        { params: defaultOptions }
      );

      return response.data;
    } catch (error: unknown) {
      console.error('❌ [StateMachineService] Failed to validate state machine:', error);
      throw error;
    }
  }

  /**
   * Get available transitions for an artifact item
   */
  static async getAvailableTransitions(
    artifactId: string,
    itemId: string
  ): Promise<AvailableTransition[]> {
    try {
      const response = await apiClient.get(
        `/artifacts/${artifactId}/items/${itemId}/available-transitions`
      );

      // Handle different response formats
      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data?.transitions) {
        return response.data.transitions;
      } else if (response.data?.available_transitions) {
        return response.data.available_transitions;
      }

      return [];
    } catch (error: unknown) {
      console.error('❌ [StateMachineService] Failed to get available transitions:', error);
      return [];
    }
  }

  /**
   * Validate if a transition can be executed on an item
   */
  static async validateTransition(
    artifactId: string,
    itemId: string,
    transitionId: string
  ): Promise<TransitionValidationResult> {
    try {
      const response = await apiClient.post(
        `/artifacts/${artifactId}/items/${itemId}/transitions/${transitionId}/validate`
      );

      return response.data;
    } catch (error: unknown) {
      console.error('❌ [StateMachineService] Failed to validate transition:', error);
      throw error;
    }
  }

  /**
   * Execute a transition on an artifact item
   */
  static async executeTransition(
    artifactId: string,
    itemId: string,
    transitionId: string,
    data: TransitionExecuteRequest
  ): Promise<any> {
    try {
      const response = await apiClient.post(
        `/artifacts/${artifactId}/items/${itemId}/transitions/${transitionId}/execute`,
        data
      );

      return response.data;
    } catch (error: unknown) {
      console.error('❌ [StateMachineService] Failed to execute transition:', error);
      throw error;
    }
  }

  /**
   * Rollback a transition for an artifact item
   */
  static async rollbackTransition(
    artifactId: string,
    itemId: string,
    data: TransitionRollbackRequest
  ): Promise<any> {
    try {
      const response = await apiClient.post(
        `/artifacts/${artifactId}/items/${itemId}/rollback-transition`,
        data
      );

      return response.data;
    } catch (error: unknown) {
      console.error('❌ [StateMachineService] Failed to rollback transition:', error);
      throw error;
    }
  }

  /**
   * Get state details by ID
   */
  static async getStateDetails(stateId: string): Promise<ArtifactItemState | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.STATE_MACHINE.STATE_BY_ID(stateId));
      return response.data;
    } catch (error: unknown) {
      console.error('❌ [StateMachineService] Failed to get state details:', error);
      return null;
    }
  }

  /**
   * Update state
   */
  static async updateState(
    stateId: string,
    data: Partial<ArtifactItemState>
  ): Promise<ArtifactItemState> {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.STATE_MACHINE.STATE_BY_ID(stateId),
        data as Record<string, unknown>
      );
      return response.data;
    } catch (error: unknown) {
      console.error('❌ [StateMachineService] Failed to update state:', error);
      throw error;
    }
  }

  /**
   * Delete state
   */
  static async deleteState(stateId: string): Promise<void> {
    try {
      await apiClient.delete(API_ENDPOINTS.STATE_MACHINE.STATE_BY_ID(stateId));
    } catch (error: unknown) {
      console.error('❌ [StateMachineService] Failed to delete state:', error);
      throw error;
    }
  }

  /**
   * Get transition details by ID
   */
  static async getTransitionDetails(transitionId: string): Promise<ArtifactItemTransition | null> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.STATE_MACHINE.TRANSITION_BY_ID(transitionId));
      return response.data;
    } catch (error: unknown) {
      console.error('❌ [StateMachineService] Failed to get transition details:', error);
      return null;
    }
  }
}

export { StateMachineService };

